import * as React from "react"
import { Ban, CheckCircle2, Clock, Loader2, PencilLine, TriangleAlert, XCircle } from "lucide-react"
import { Badge } from "@/components/ui/badge"

// ---------------------------------------------------------------------------
// Status
// ---------------------------------------------------------------------------

export type BriefStatus =
  | "pending"
  | "computing"
  | "signals_ready"
  | "narrating"
  | "draft"
  | "needs_review"
  | "published"
  | "failed"
  | "cancelled"

// The pipeline is actively running in these states. signals_ready is "active"
// only when the narration layer is on (the job will continue to narrating).
const BASE_ACTIVE: BriefStatus[] = ["pending", "computing", "narrating"]

export function isActiveStatus(status: BriefStatus, assemblerEnabled: boolean): boolean {
  if (BASE_ACTIVE.includes(status)) return true
  return assemblerEnabled && status === "signals_ready"
}

const STATUS_META: Record<
  BriefStatus,
  { label: string; tone: React.ComponentProps<typeof Badge>["tone"]; icon: React.ComponentType<{ className?: string }>; spin?: boolean }
> = {
  pending:       { label: "Antre",        tone: "muted",   icon: Clock },
  computing:     { label: "Menghitung",   tone: "accent",  icon: Loader2, spin: true },
  signals_ready: { label: "Sinyal siap",  tone: "neutral", icon: CheckCircle2 },
  narrating:     { label: "Menarasikan",  tone: "accent",  icon: Loader2, spin: true },
  draft:         { label: "Draf",         tone: "accent",  icon: PencilLine },
  needs_review:  { label: "Perlu Tinjau", tone: "signal",  icon: TriangleAlert },
  published:     { label: "Terbit",       tone: "success", icon: CheckCircle2 },
  failed:        { label: "Gagal",        tone: "danger",  icon: XCircle },
  cancelled:     { label: "Dibatalkan",   tone: "muted",   icon: Ban },
}

export function StatusBadge({ status }: { status: BriefStatus }) {
  const meta = STATUS_META[status] ?? STATUS_META.pending
  const Icon = meta.icon
  return (
    <Badge tone={meta.tone}>
      <Icon className={`mr-1 h-3 w-3 ${meta.spin ? "animate-spin" : ""}`} />
      {meta.label}
    </Badge>
  )
}

export function confidenceTone(confidence: string): React.ComponentProps<typeof Badge>["tone"] {
  if (confidence === "tinggi") return "success"
  if (confidence === "rendah") return "danger"
  return "signal"
}

export function priorityTone(priority: string): React.ComponentProps<typeof Badge>["tone"] {
  if (priority === "high") return "danger"
  if (priority === "low") return "muted"
  return "signal"
}

export function actionStatusTone(status: string): React.ComponentProps<typeof Badge>["tone"] {
  if (status === "done") return "success"
  if (status === "in_progress") return "accent"
  return "muted"
}

// ---------------------------------------------------------------------------
// Citations
// ---------------------------------------------------------------------------

export type Citation = {
  source_kind: string | null
  source_id: number | null
  dataset: string | null
  period: string | null
}

// source_kind → human label + best-effort link to that dataset's uploads page.
// Unknown kinds render as an informational (non-link) chip.
const SOURCE_META: Record<string, { label: string; href: string | null }> = {
  market_share_b2b_upload:  { label: "Market Share B2B", href: "/admin/market-share-b2b" },
  b2b_service_level_upload: { label: "Service Level",    href: "/admin/b2b/service-level" },
  b2b_stock_upload:         { label: "Stock",            href: "/admin/b2b/stock" },
  niq_fragrance_upload:     { label: "NIQ Fragrances",   href: "/admin/niq-fragrances" },
  ka_profitability_upload:  { label: "KA Profitability", href: "/admin/data/ka-profitability/uploads" },
}

function sourceMeta(kind: string | null): { label: string; href: string | null } {
  return (kind && SOURCE_META[kind]) || { label: kind ?? "Sumber", href: null }
}

// A cited number: the token plus a click-to-open popover listing its sources.
// SSR-safe: state lives in the component, no window access at module scope.
function CitationChip({ token, citations }: { token: string; citations: Citation[] }) {
  const [open, setOpen] = React.useState(false)
  return (
    <span className="relative inline-block">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="rounded bg-accent-faded px-0.5 font-medium text-accent underline decoration-dotted decoration-accent/50 underline-offset-2 tabular-nums"
        aria-label={`Sumber untuk angka ${token}`}
        aria-expanded={open}
      >
        {token}
      </button>
      {open && (
        <>
          <span className="fixed inset-0 z-40" aria-hidden onClick={() => setOpen(false)} />
          <span className="absolute left-0 top-full z-50 mt-1 block w-64 rounded-md border border-hairline bg-surface p-3 text-xs shadow-lg">
            <span className="mb-1.5 block font-medium text-ink-display">Sumber angka {token}</span>
            <ul className="space-y-1.5">
              {citations.map((c, i) => {
                const meta = sourceMeta(c.source_kind)
                return (
                  <li key={i} className="leading-snug">
                    {meta.href ? (
                      <a href={meta.href} className="text-accent hover:underline">{meta.label}</a>
                    ) : (
                      <span className="text-ink-body">{meta.label}</span>
                    )}
                    <span className="text-ink-muted">
                      {c.period ? ` · ${c.period}` : ""}
                      {c.source_id ? ` · #${c.source_id}` : ""}
                    </span>
                  </li>
                )
              })}
            </ul>
          </span>
        </>
      )}
    </span>
  )
}

// A number that the validator could NOT ground (only on needs_review briefs).
function UnverifiedChip({ token }: { token: string }) {
  return (
    <span
      className="rounded bg-amber-100 px-0.5 font-medium text-amber-700 dark:bg-amber-900/40 dark:text-amber-300 tabular-nums"
      title="Angka ini tidak dapat dicocokkan ke context pack — perlu ditinjau."
    >
      {token}
    </span>
  )
}

const NUMBER_RE = /(\d[\d.,]*\d|\d)/

// Render prose with each numeric token wrapped in a citation chip (or, on a
// needs_review brief, an unverified-number warning chip).
export function AnnotatedText({
  text,
  citationIndex,
  unknownTokens = [],
}: {
  text: string | null | undefined
  citationIndex: Record<string, Citation[]>
  unknownTokens?: string[]
}) {
  if (!text) return null
  const parts = text.split(NUMBER_RE)
  return (
    <>
      {parts.map((part, i) => {
        // Odd indices are the captured numeric tokens.
        if (i % 2 === 1) {
          const cites = citationIndex[part]
          if (cites && cites.length > 0) return <CitationChip key={i} token={part} citations={cites} />
          if (unknownTokens.includes(part)) return <UnverifiedChip key={i} token={part} />
          return <React.Fragment key={i}>{part}</React.Fragment>
        }
        return <React.Fragment key={i}>{part}</React.Fragment>
      })}
    </>
  )
}
